import {
  CheckCircle,
  XCircle,
  Calendar,
  Info,
  PhoneForwarded,
  AlertTriangle,
  ArrowRightLeft,
  CreditCard,
} from 'lucide-react';
import { statusLabel } from '../i18n/tr';

const BOOKING_PHRASES = ['randevu oluştur', 'randevu kayd', 'randevu onay', 'appointment created'];
const EMERGENCY_PHRASES = ['112', 'acil servis', 'acil durum', 'emergency'];

function isAppointmentConfirmed(appt) {
  if (!appt) return false;
  return appt.confirmed === true || appt.status === 'confirmed';
}

function hasBookedAppointmentFields(appt) {
  if (!appt) return false;
  return Boolean(appt.date && appt.time && (appt.department || appt.plate));
}

function routingStatusFromTarget(targetId) {
  if (!targetId) return null;
  const id = String(targetId).toLowerCase();
  if (id.includes('emergency') || id.includes('acil')) return 'Emergency Redirect';
  if (id.includes('billing') || id.includes('muhasebe')) return 'Routed to Billing';
  if (id.includes('patient') || id.includes('triage') || id.includes('mts') || id.includes('sks')) {
    return 'Routed to Patient Relations';
  }
  return 'Transferred';
}

export function resolveDisplayStatus(call) {
  const { summary = '', appointment, transfer, status: storedStatus } = call;
  const s = summary.toLowerCase();

  if (EMERGENCY_PHRASES.some((p) => s.includes(p))) return 'Emergency Redirect';

  const routeStatus = routingStatusFromTarget(transfer?.targetId || appointment?.routedTo);
  if (routeStatus && (transfer?.completed || transfer?.answered)) return routeStatus;

  if (storedStatus === 'Routed to Patient Relations' || storedStatus === 'Routed to MTS/SKS') {
    return 'Routed to Patient Relations';
  }
  if (storedStatus === 'Routed to Billing') return 'Routed to Billing';
  if (storedStatus === 'Emergency Redirect') return 'Emergency Redirect';
  if (storedStatus === 'Transferred') return 'Transferred';

  if (isAppointmentConfirmed(appointment)) return 'Appointment Created';
  if (hasBookedAppointmentFields(appointment) && appointment.status !== 'cancelled') {
    return 'Appointment Created';
  }
  if (storedStatus === 'Appointment Created') return 'Appointment Created';
  if (BOOKING_PHRASES.some((p) => s.includes(p))) return 'Appointment Created';
  if (storedStatus === 'Appointment Requested') return 'Appointment Requested';
  if (s.includes('randevu') || s.includes('appointment')) return 'Appointment Requested';
  if (storedStatus === 'Info Provided' || s.includes('bilgi')) return 'Info Provided';
  if (storedStatus === 'Failed') return 'Failed';
  return storedStatus || 'Info Provided';
}

export function getStatusStyle(status) {
  switch (status) {
    case 'Success':
    case 'Appointment Created':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'Appointment Requested':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'Routed to Patient Relations':
    case 'Routed to MTS/SKS':
      return 'bg-sky-500/15 text-sky-300 border-sky-500/30';
    case 'Routed to Billing':
      return 'bg-violet-500/15 text-violet-300 border-violet-500/30';
    case 'Emergency Redirect':
      return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
    case 'Transferred':
      return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
    case 'Info Provided':
      return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'Failed':
      return 'bg-rose-500/15 text-rose-400 border-rose-500/30';
    default:
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
  }
}

export function getStatusIcon(status, className = 'w-4 h-4') {
  switch (status) {
    case 'Success':
    case 'Appointment Created':
      return <CheckCircle className={className} />;
    case 'Appointment Requested':
      return <Calendar className={className} />;
    case 'Routed to Patient Relations':
    case 'Routed to MTS/SKS':
      return <ArrowRightLeft className={className} />;
    case 'Routed to Billing':
      return <CreditCard className={className} />;
    case 'Emergency Redirect':
      return <AlertTriangle className={className} />;
    case 'Transferred':
      return <PhoneForwarded className={className} />;
    case 'Info Provided':
      return <Info className={className} />;
    case 'Failed':
      return <XCircle className={className} />;
    default:
      return <Info className={className} />;
  }
}

export { statusLabel };
